import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { evaluateSchoolSubscription } from '../../lib/subscription';
import { savePersistedLogin, isTeacherOrStudentRole } from '../../lib/persistedAuth';
import './OTPVerification.css';
import Logo from '../../assets/Logo.svg';

const OTPVerification = () => {
  const [otp, setOtp] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes in seconds
  const [pendingLogin, setPendingLogin] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Get pending login data from sessionStorage
    const pendingLoginStr = sessionStorage.getItem('pendingLogin');
    if (!pendingLoginStr) {
      navigate('/');
      return;
    }

    try {
      const loginData = JSON.parse(pendingLoginStr);
      setPendingLogin(loginData);
      setUserEmail(loginData.userData.email);
      
      // Check if OTP has expired
      const now = new Date();
      const expiry = new Date(loginData.otpExpiry);
      if (now > expiry) {
        setError('OTP has expired. Please request a new one.');
        sessionStorage.removeItem('pendingLogin');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      // Set up countdown timer
      const interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            clearInterval(interval);
            setError('OTP has expired. Please request a new one.');
            sessionStorage.removeItem('pendingLogin');
            setTimeout(() => navigate('/'), 3000);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(interval);
    } catch (err) {navigate('/');
    }
  }, [navigate]);

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setOtp(value);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    if (!pendingLogin) {
      setError('Session expired. Please try again.');
      navigate('/');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Check if OTP has expired
      const now = new Date();
      const expiry = new Date(pendingLogin.otpExpiry);
      if (now > expiry) {
        setError('OTP has expired. Please request a new one.');
        sessionStorage.removeItem('pendingLogin');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      // Check attempt limit
      if (pendingLogin.attempts >= 3) {
        setError('Too many failed attempts. Please request a new OTP.');
        sessionStorage.removeItem('pendingLogin');
        setTimeout(() => navigate('/'), 3000);
        return;
      }

      // Verify OTP
      if (otp === pendingLogin.otp) {// Final subscription gate (in case it changed between ID lookup and OTP entry)
        const sub = evaluateSchoolSubscription(pendingLogin?.userData?.schools);
        if (!sub.isAccessible) {
          setError('Account Currently Unavailable');
          sessionStorage.removeItem('pendingLogin');
          setTimeout(() => navigate('/?error=account-unavailable'), 800);
          return;
        }
        
        // Store user data in sessionStorage for the providers page
        sessionStorage.setItem('currentUser', JSON.stringify(pendingLogin.userData));
        sessionStorage.removeItem('pendingLogin');

        // Persist teacher/student login for 1 week for faster future access.
        if (isTeacherOrStudentRole(pendingLogin?.userData?.role)) {
          savePersistedLogin(pendingLogin.userData);
        }
        
        setSuccess('Login successful! Redirecting...');
        
        // Navigate to providers page after a short delay
        setTimeout(() => {
          navigate('/providers');
        }, 1500);
        
      } else {
        // Increment attempts
        const updatedLogin = {
          ...pendingLogin,
          attempts: pendingLogin.attempts + 1
        };
        sessionStorage.setItem('pendingLogin', JSON.stringify(updatedLogin));
        setPendingLogin(updatedLogin);
        
        const remainingAttempts = 3 - updatedLogin.attempts;
        if (remainingAttempts > 0) {
          setError(`Invalid OTP. ${remainingAttempts} attempts remaining.`);
        } else {
          setError('Too many failed attempts. Please request a new OTP.');
          sessionStorage.removeItem('pendingLogin');
          setTimeout(() => navigate('/'), 3000);
        }
      }
    } catch (err) {setError('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (!pendingLogin) return;
    
    setIsLoading(true);
    setError('');

    try {
      // Generate new OTP
      const newOtp = Math.floor(100000 + Math.random() * 900000).toString();
      const otpExpiry = new Date(Date.now() + 5 * 60 * 1000); // 5 minutes from now
      
      // Update pending login with new OTP
      const updatedLogin = {
        ...pendingLogin,
        otp: newOtp,
        otpExpiry: otpExpiry.toISOString(),
        attempts: 0
      };
      
      sessionStorage.setItem('pendingLogin', JSON.stringify(updatedLogin));
      setPendingLogin(updatedLogin);
      setTimeLeft(300); // Reset timer
      setOtp(''); // Clear input
      
      // Send new OTP email
      try {
        const { error: emailError } = await supabase.functions.invoke('send-otp', {
          body: {
            email: pendingLogin.userData.email,
            otp: newOtp,
            userName: pendingLogin.userData.full_name || `${pendingLogin.userData.first_name} ${pendingLogin.userData.last_name}`
          }
        });
        
        if (emailError) {alert(`New OTP sent! Check your email. (Dev OTP: ${newOtp})`);
        } else {setSuccess('New OTP sent to your email!');
        }
      } catch (emailErr) {alert(`New OTP sent! Check your email. (Dev OTP: ${newOtp})`);
      }
      
    } catch (err) {setError('Failed to resend OTP. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!pendingLogin) {
    return (
      <div className="otp-page">
        <div className="otp-loading">
          <div className="loading-spinner">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z"/>
            </svg>
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="otp-page">
      <div className="otp-container">
        <div className="otp-header">
          <div className="otp-logo">
            <img src={Logo} alt="MyDigitalSchool Logo" className="otp-logo-image" />
          </div>
          <h1 className="otp-title">Verify Your Identity</h1>
          <p className="otp-subtitle">
            We've sent a 6-digit verification code to<br />
            <strong>{userEmail}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="otp-form">
          <div className="otp-input-group">
            <label htmlFor="otp" className="otp-label">Enter Verification Code</label>
            <input
              type="text"
              id="otp"
              value={otp}
              onChange={handleOtpChange}
              placeholder="000000"
              className="otp-input"
              maxLength="6"
              disabled={isLoading}
              autoComplete="one-time-code"
            />
          </div>

          {error && (
            <div className="otp-error">
              <span className="error-icon">⚠️</span>
              <span className="error-text">{error}</span>
            </div>
          )}

          {success && (
            <div className="otp-success">
              <span className="success-icon">✅</span>
              <span className="success-text">{success}</span>
            </div>
          )}

          <div className="otp-timer">
            <span className="timer-text">
              Code expires in: <strong>{formatTime(timeLeft)}</strong>
            </span>
          </div>

          <button
            type="submit"
            className={`otp-submit-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading || otp.length !== 6}
          >
            {isLoading ? (
              <>
                <div className="loading-spinner">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="31.416">
                      <animate attributeName="stroke-dasharray" dur="2s" values="0 31.416;15.708 15.708;0 31.416" repeatCount="indefinite"/>
                      <animate attributeName="stroke-dashoffset" dur="2s" values="0;-15.708;-31.416" repeatCount="indefinite"/>
                    </circle>
                  </svg>
                </div>
                Verifying...
              </>
            ) : (
              'Verify & Continue'
            )}
          </button>

          <div className="otp-actions">
            <button
              type="button"
              className="otp-resend-btn"
              onClick={handleResendOtp}
              disabled={isLoading}
            >
              Didn't receive the code? Resend
            </button>
            
            <button
              type="button"
              className="otp-back-btn"
              onClick={() => navigate('/')}
              disabled={isLoading}
            >
              ← Back to Login
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OTPVerification;
